/**
 * Image Validation Utility
 * Validates base64 image data for security and integrity
 * Issue #3: Unsafe Image Data Handling
 */

// Constants for image validation
export const IMAGE_VALIDATION_CONFIG = {
    MAX_SIZE_BYTES: 10 * 1024 * 1024, // 10MB
    MIN_SIZE_BYTES: 100, // Minimum valid image size
    ALLOWED_MIME_TYPES: ["image/jpeg", "image/png", "image/gif", "image/webp"],
    MAX_DIMENSION: 4096, // Maximum width/height in pixels
};

/**
 * Validates base64 image data for security and integrity
 * @param {string} imageData - Base64 image data URL
 * @returns {Object} - { valid: boolean, base64Data?: string, mimeType?: string, error?: string, sizeInBytes?: number }
 */
export const validateBase64Image = (imageData) => {
    // Step 1: Basic type validation
    if (!imageData || typeof imageData !== "string") {
        return { valid: false, error: "Invalid image data: must be a non-empty string" };
    }

    // Step 2: Validate data URL format with strict regex
    // Matches: data:image/(jpeg|png|gif|webp);base64,<base64data>
    const dataUrlRegex = /^data:(image\/(jpeg|png|gif|webp));base64,([A-Za-z0-9+/]+={0,2})$/;
    const match = imageData.match(dataUrlRegex);

    if (!match) {
        return { valid: false, error: "Invalid image format: must be a valid data URL with base64 encoding" };
    }

    const mimeType = match[1];
    const base64Data = match[3];

    // Step 3: Validate MIME type against allowed list
    if (!IMAGE_VALIDATION_CONFIG.ALLOWED_MIME_TYPES.includes(mimeType)) {
        return { valid: false, error: `Unsupported image type: ${mimeType}` };
    }

    // Step 4: Validate base64 data is not empty
    if (!base64Data || base64Data.length === 0) {
        return { valid: false, error: "Empty image data" };
    }

    // Step 5: Calculate and validate size
    // Base64 encoding increases size by ~33%, so actual bytes = base64Length * 3/4 - padding
    const paddingCount = base64Data.endsWith("==") ? 2 : base64Data.endsWith("=") ? 1 : 0;
    const sizeInBytes = (base64Data.length * 3) / 4 - paddingCount;

    if (sizeInBytes < IMAGE_VALIDATION_CONFIG.MIN_SIZE_BYTES) {
        return { valid: false, error: "Image too small: may be corrupted" };
    }

    if (sizeInBytes > IMAGE_VALIDATION_CONFIG.MAX_SIZE_BYTES) {
        return {
            valid: false,
            error: `Image too large: ${(sizeInBytes / 1024 / 1024).toFixed(2)}MB exceeds ${IMAGE_VALIDATION_CONFIG.MAX_SIZE_BYTES / 1024 / 1024}MB limit`,
        };
    }

    // Step 6: Validate base64 characters (no injection)
    const validBase64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
    if (!validBase64Regex.test(base64Data)) {
        return { valid: false, error: "Invalid base64 encoding: contains illegal characters" };
    }

    return {
        valid: true,
        base64Data,
        mimeType,
        sizeInBytes,
    };
};

/**
 * Extracts and validates base64 data from image string
 * Handles both data URL format and raw base64
 * @param {string} imageData - Image data (data URL or raw base64)
 * @returns {Object} - { success: boolean, data?: string, mimeType?: string, error?: string }
 */
export const extractBase64Data = (imageData) => {
    if (!imageData || typeof imageData !== "string") {
        return { success: false, error: "Invalid input" };
    }

    // If it's a data URL, extract the base64 portion
    if (imageData.includes(",")) {
        const validation = validateBase64Image(imageData);
        if (!validation.valid) {
            return { success: false, error: validation.error };
        }
        return { success: true, data: validation.base64Data, mimeType: validation.mimeType };
    }

    // If raw base64, validate format
    const validBase64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
    if (!validBase64Regex.test(imageData)) {
        return { success: false, error: "Invalid base64 format" };
    }

    // Check minimum size for raw base64
    const paddingCount = imageData.endsWith("==") ? 2 : imageData.endsWith("=") ? 1 : 0;
    const sizeInBytes = (imageData.length * 3) / 4 - paddingCount;

    if (sizeInBytes < IMAGE_VALIDATION_CONFIG.MIN_SIZE_BYTES) {
        return { success: false, error: "Image data too small" };
    }

    if (sizeInBytes > IMAGE_VALIDATION_CONFIG.MAX_SIZE_BYTES) {
        return { success: false, error: "Image data too large" };
    }

    return { success: true, data: imageData, mimeType: "image/jpeg" };
};

export default { validateBase64Image, extractBase64Data, IMAGE_VALIDATION_CONFIG };
